<template>
  <v-card class="rounded-t-0 pa-6" style="border-top: 2px solid rgb(var(--v-theme-primary));">
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center ga-2 flex-wrap">
        <span class="text-caption text-medium-emphasis">
          {{ channel.type.sector_name }} ({{ channel.type.section_name }})
        </span>
        <v-icon size="x-small" color="medium-emphasis">
          fa-solid fa-chevron-right
        </v-icon>
        <span class="text-subtitle-1 font-weight-semibold">{{ channel.type.name }}</span>
        <v-chip size="small" label>
          {{ channel.pos.name }}
        </v-chip>
      </div>
      <v-btn
        icon
        size="small"
        variant="text"
        @click="emit('close')"
      >
        <v-icon size="small">
          fa-solid fa-xmark
        </v-icon>
      </v-btn>
    </div>

    <batch-channel-timeline
      v-if="channelBatches.length > 0"
      :batches="channelBatches"
      :event="event"
      :editing-batch-id="editingBatch?.id ?? null"
      @select="startEdit"
    />

    <v-table v-if="channelBatches.length > 0" density="compact">
      <thead>
        <tr>
          <th>Lote</th>
          <th>Status</th>
          <th>Preço</th>
          <th>Quantidade</th>
          <th>Período de Vendas</th>
          <th>Virada Automática</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="batch in channelBatches"
          :key="batch.id"
          :class="{ 'editing-row': editingBatch?.id === batch.id }"
        >
          <td>{{ batch.number }}</td>
          <td>
            <v-chip :color="statusColor(batch)" size="x-small" label>
              {{ statusLabel(batch) }}
            </v-chip>
          </td>
          <td>{{ currency(batch.price) }}</td>
          <td>{{ batch.quantity ?? 'Ilimitado' }}</td>
          <td class="text-caption">
            {{ formatPeriod(batch) }}
          </td>
          <td>
            <v-icon v-if="batch.auto_advance" size="x-small" color="success">
              fa-solid fa-check
            </v-icon>
            <span v-else class="text-medium-emphasis">—</span>
          </td>
          <td class="text-right">
            <v-btn
              icon="fa-solid fa-pen"
              color="info"
              size="small"
              variant="text"
              class="mr-1"
              @click="startEdit(batch)"
            />
            <v-btn
              icon="fa-solid fa-trash"
              color="error"
              size="small"
              variant="text"
              @click="emit('requestDelete', batch)"
            />
          </td>
        </tr>
      </tbody>
    </v-table>

    <v-divider class="mb-5" />

    <div v-if="!formOpen" class="d-flex justify-end">
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="fa-solid fa-plus"
        @click="openCreateForm"
      >
        Novo Lote
      </v-btn>
    </div>

    <template v-if="formOpen">
      <div class="d-flex align-center ga-2 mb-4">
        <span class="text-subtitle-2">
          {{ editingBatch ? `Editando Lote ${editingBatch.number}` : 'Novo Lote' }}
        </span>
      </div>

      <v-form ref="form">
        <v-row>
          <v-col cols="12" sm="3">
            <currency-field
              v-model="formData.price"
              variant="outlined"
              label="Preço"
              :disabled="isFreeType"
              density="compact"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="formData.quantity"
              type="number"
              label="Quantidade"
              variant="outlined"
              :min="0"
              :rules="quantityRules"
              :disabled="formData.unlimited"
              density="compact"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <date-time-field
              v-model="formData.starts_at"
              label="Início das Vendas"
              :rules="startsAtRules"
              :disabled="!formData.usePeriod"
              :seconds="false"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <date-time-field
              v-model="formData.ends_at"
              label="Fim das Vendas"
              :rules="endsAtRules"
              :disabled="!formData.usePeriod"
              :seconds="false"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <t-checkbox
              v-model="formData.usePeriod"
              text="Utilizar o período de vendas"
              :disabled="periodRequired"
            />
            <div class="text-caption text-medium-emphasis pl-1">
              <div>
                <strong>Desativado (padrão):</strong> o lote fica aberto até esgotar e a virada para o próximo lote é feita apenas pela quantidade.
              </div>
              <div>
                <strong>Ativado:</strong> o lote respeita as datas definidas, mas ainda pode virar antes do previsto se a virada automática estiver ativa.
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <t-checkbox
              v-model="formData.unlimited"
              text="Ilimitado"
              :disabled="formData.auto_advance"
            />
            <div class="text-caption text-medium-emphasis pl-1">
              <div>
                <strong>Desativado (padrão):</strong> a quantidade informada é o limite de vendas do lote neste PDV.
              </div>
              <div>
                <strong>Ativado:</strong> o lote nunca esgota, mas continua sendo limitado pela capacidade máxima da seção.
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <t-checkbox
              v-model="formData.auto_advance"
              text="Virada Automática"
              :disabled="formData.unlimited"
            />
            <div class="text-caption text-medium-emphasis pl-1">
              <strong>Ativado:</strong> ao esgotar, o próximo lote do canal (na ordem de criação) assume automaticamente, mesmo que sua data de início ainda não tenha chegado.
            </div>
          </v-col>
        </v-row>
      </v-form>

      <v-alert
        v-for="warning in formWarnings"
        :key="warning"
        type="warning"
        density="compact"
        variant="tonal"
        class="mt-2"
      >
        {{ warning }}
      </v-alert>

      <v-expansion-panels
        v-if="otherPosList.length > 0 && !editingBatch"
        v-model="replicateOpen"
        class="mt-4"
        variant="accordion"
      >
        <v-expansion-panel>
          <v-expansion-panel-title class="text-body-2">
            Replicar lote em outros PDVs
            <v-chip
              v-if="replicateToPosIds.length > 0"
              size="x-small"
              color="primary"
              label
              class="ml-2"
            >
              {{ replicateToPosIds.length }}
            </v-chip>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-alert
              type="info"
              density="compact"
              variant="tonal"
              class="mb-3"
            >
              O lote será criado em {{ props.channel.pos.name }} e nos outros PDVs selecionados abaixo.
            </v-alert>

            <v-checkbox
              v-for="pos in otherPosList"
              :key="pos.id"
              v-model="replicateToPosIds"
              :value="pos.id"
              :label="pos.name"
              density="compact"
              hide-details
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="d-flex ga-2 mt-4 justify-end">
        <v-btn
          color="primary"
          :loading="loading"
          @click="submit"
        >
          {{ editingBatch ? 'Salvar Alterações' : 'Criar Lote' }}
        </v-btn>
        <v-btn variant="text" :disabled="loading" @click="cancelEdit">
          Cancelar
        </v-btn>
      </div>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import BatchChannelTimeline from './batch-channel-timeline.vue'
import CurrencyField from '@/components/form/CurrencyField.vue'
import DateTimeField from '@/components/form/DateTimeField.vue'
import TCheckbox from '@/components/form/TCheckbox.vue'
import { FreeTypes } from '@/constants/TicketTypeNames'
import { formatPeriod, parseDate, statusColor, statusLabel, toFieldFormat } from '@/helpers/batches'
import TicketBatchService from '@/services/TicketBatchService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import type Event from '@/types/Event'
import type PointOfSale from '@/types/PointOfSale'
import type TicketBatch from '@/types/TicketBatch'
import type { TicketBatchPayload } from '@/types/TicketBatch'
import type TicketType from '@/types/TicketType'
import { currency } from '@/helpers/formatters/currency'
import { VForm } from 'vuetify/components'
import axios from 'axios'

defineOptions({ name: 'BatchChannelEditor' })

interface Channel {
  type: TicketType
  pos: PointOfSale
}

const props = defineProps<{
  channel: Channel
  event: Event
  batchGrid: Record<string, Record<string, TicketBatch[]>>
  ticketTypes: TicketType[]
  posList: PointOfSale[]
  eventId: string
}>()

const emit = defineEmits<{
  saved: []
  requestDelete: [batch: TicketBatch]
  close: []
}>()

const loader = useLoader()
const snackbar = useSnackBar()
const service = new TicketBatchService()

// --- Form state ---

interface FormData {
  price: number
  quantity: number
  unlimited: boolean
  usePeriod: boolean
  starts_at: string | null
  ends_at: string | null
  auto_advance: boolean
}

const editingBatch = ref<TicketBatch | null>(null)
const form = ref<InstanceType<typeof VForm> | null>(null)
const formData = ref<FormData>({ price: 0, quantity: 0, unlimited: false, usePeriod: false, starts_at: null, ends_at: null, auto_advance: false })
const replicateToPosIds = ref<string[]>([])
const replicateOpen = ref<number | undefined>(undefined)
const formOpen = ref<boolean>(false)
const loading = computed(() => loader.loading)

// --- Derived ---

const isFreeType = computed(() => FreeTypes.includes(props.channel.type.name))

const channelBatches = computed<TicketBatch[]>(() =>
  props.batchGrid[props.channel.type.id]?.[props.channel.pos.id] ?? [],
)

const otherPosList = computed<PointOfSale[]>(() =>
  props.posList.filter(p => p.name !== 'Especiais' && p.id !== props.channel.pos.id),
)

const primaryChannelRequiresPeriod = computed<boolean>(() =>
  channelPredecessorGetsStuck(channelBatches.value, editingBatch.value),
)

const replicateTargetsRequiringPeriod = computed<PointOfSale[]>(() =>
  otherPosList.value.filter(pos =>
    replicateToPosIds.value.includes(pos.id)
    && channelPredecessorGetsStuck(props.batchGrid[props.channel.type.id]?.[pos.id] ?? [], null),
  ),
)

const periodRequired = computed<boolean>(() =>
  primaryChannelRequiresPeriod.value || replicateTargetsRequiringPeriod.value.length > 0,
)

function channelPredecessorGetsStuck(batches: TicketBatch[], editing: TicketBatch | null): boolean {
  const targetNumber = editing?.number ?? null

  const predecessor = batches
    .filter(b => b.id !== editing?.id && (targetNumber === null || b.number < targetNumber))
    .sort((a, b) => b.number - a.number)[0]

  if (!predecessor) return false

  return predecessor.starts_at === null && (!predecessor.auto_advance || predecessor.quantity === null)
}

// --- Validation rules ---

const quantityRules = computed(() => [
  (v: number) => formData.value.unlimited || v > 0 || 'A quantidade não pode ser menor que 1.',
])

const startsAtRules = computed(() => [
  (v: string | null) => !formData.value.usePeriod || !!v || 'O início das vendas é obrigatório.',
  // Fails for starts_at < event's sales start
  (v: string | null) => {
    if (!formData.value.usePeriod || !v) return true

    const batchStart = parseDate(v)
    const eventSalesStart = parseDate(props.event.dates.sales.open ?? null)

    if (batchStart && eventSalesStart && batchStart < eventSalesStart) {
      return 'O início precede o período de vendas do evento.'
    }

    return true
  },
])

const endsAtRules = computed(() => [
  (v: string | null) => !formData.value.usePeriod || !!v || 'O fim das vendas é obrigatório.',
  // Fails for ends_at > event's sales end
  (v: string | null) => {
    if (!formData.value.usePeriod || !v) return true

    const batchEnd = parseDate(v)
    const eventSalesEnd = parseDate(props.event.dates.sales.close ?? null)

    if (batchEnd && eventSalesEnd && batchEnd > eventSalesEnd) {
      return 'O fim ultrapassa o período de vendas do evento.'
    }

    return true
  },
  // Fails for ends_at <= starts_at
  (v: string | null) => {
    if (!formData.value.usePeriod || !v || !formData.value.starts_at) return true

    const batchEnd = parseDate(v)
    const batchStart = parseDate(formData.value.starts_at)

    if (batchEnd && batchStart && batchEnd <= batchStart) {
      return 'O fim deve ser posterior ao início.'
    }

    return true
  },
])

// If the batch period partially overlaps another
const overlappingBatches = computed<TicketBatch[]>(() => {
  if (!formData.value.usePeriod || !formData.value.starts_at || !formData.value.ends_at) return []

  const formStart = parseDate(formData.value.starts_at)
  const formEnd = parseDate(formData.value.ends_at)

  if (!formStart || !formEnd) return []

  return channelBatches.value.filter((b) => {
    if (b.id === editingBatch.value?.id) return false

    const batchStart = parseDate(b.starts_at)
    const batchEnd = parseDate(b.ends_at)

    if (!batchStart || !batchEnd) return false

    return formStart < batchEnd && formEnd > batchStart
  })
})

const formWarnings = computed<string[]>(() => {
  const warnings: string[] = []

  const { price } = formData.value

  if (!isFreeType.value && price === 0) {
    warnings.push('O preço está zerado para um tipo de ingresso que não é Gratuidade nem Cortesia.')
  }

  if (overlappingBatches.value.length > 0) {
    const numbers = new Intl.ListFormat('pt-BR', { style: 'long', type: 'conjunction' })
      .format(overlappingBatches.value.map(b => b.number.toString()))

    warnings.push(
      `Este período sobrepõe o(s) lote(s) ${numbers}. O período do lote vizinho será ajustado automaticamente ao salvar.`,
    )
  }

  return warnings
})

// --- Reset form on channel change ---

watch(
  () => props.channel,
  (ch) => {
    editingBatch.value = null
    replicateToPosIds.value = []
    replicateOpen.value = undefined
    formOpen.value = false
    resetForm(ch)
  },
  { immediate: true },
)

watch(
  () => props.batchGrid,
  () => {
    if (!editingBatch.value) {
      resetForm(props.channel)
    }
  },
)

// Force the sales period on (and keep it on) once a selected replication
// target requires it — the primary channel's own requirement is instead
// handled inside resetForm, since that already runs on every channel switch.
watch(
  replicateTargetsRequiringPeriod,
  (targets) => {
    if (targets.length > 0) formData.value.usePeriod = true
  },
)

watch(
  () => formData.value.usePeriod,
  (usePeriod) => {
    if (!usePeriod) {
      formData.value.starts_at = null
      formData.value.ends_at = null
    }
  },
)

watch(
  () => formData.value.unlimited,
  (unlimited) => {
    if (unlimited) formData.value.quantity = 0
  },
)

watch(
  () => formData.value.auto_advance,
  (autoAdvance) => {
    if (autoAdvance) formData.value.unlimited = false
  },
)

async function resetForm(ch: Channel): Promise<void> {
  const batches = props.batchGrid[ch.type.id]?.[ch.pos.id] ?? []

  // No default period: sales period is opt-in (see `usePeriod`), so a new batch
  // starts undated by default and advances purely by quantity. Exception: if
  // the channel's last batch is undated and can never advance on its own, a
  // period is required.

  formData.value = {
    price: Math.round((ch.type.price ?? 0) * 100),
    quantity: 0,
    unlimited: false,
    usePeriod: channelPredecessorGetsStuck(batches, null),
    starts_at: null,
    ends_at: null,
    auto_advance: false,
  }

  await nextTick()

  form.value?.resetValidation()
}

// --- Form modes ---

function openCreateForm(): void {
  editingBatch.value = null
  replicateToPosIds.value = []
  resetForm(props.channel)
  formOpen.value = true
}

function startEdit(batch: TicketBatch): void {
  editingBatch.value = batch
  replicateToPosIds.value = []
  formOpen.value = true
  formData.value = {
    price: Math.round(batch.price * 100),
    quantity: batch.quantity ?? 0,
    unlimited: batch.quantity === null,
    usePeriod: batch.starts_at !== null,
    starts_at: toFieldFormat(batch.starts_at),
    ends_at: toFieldFormat(batch.ends_at),
    auto_advance: batch.auto_advance,
  }
}

function cancelEdit(): void {
  editingBatch.value = null
  replicateToPosIds.value = []
  formOpen.value = false
  resetForm(props.channel)
}

function finishSave(): void {
  const wasEditing = editingBatch.value !== null
  editingBatch.value = null
  replicateToPosIds.value = []

  resetForm(props.channel)

  if (wasEditing) {
    formOpen.value = false
  }

  emit('saved')
}

// --- Save ---

async function submit(): Promise<void> {
  const { valid: formValid } = await form.value!.validate()

  if (!formValid) return

  const { type, pos } = props.channel
  const primaryPosId = pos.id
  const replicateTargets = replicateToPosIds.value

  const payload: TicketBatchPayload = {
    price: formData.value.price / 100,
    quantity: formData.value.unlimited ? null : formData.value.quantity,
    starts_at: formData.value.usePeriod ? formData.value.starts_at : null,
    ends_at: formData.value.usePeriod ? formData.value.ends_at : null,
    auto_advance: formData.value.auto_advance,
  }

  try {
    loader.increment()

    if (editingBatch.value) {
      await service.update(props.eventId, editingBatch.value.id, payload)

      snackbar.show('Lote atualizado com sucesso.', 'success')
    } else {
      // Create on primary and replicate targets
      const posIds = [primaryPosId, ...replicateTargets]

      await Promise.all(
        posIds.map(posId => service.store(props.eventId, type.id, posId, payload)),
      )

      snackbar.show(
        replicateTargets.length > 0
          ? `Lote criado em ${posIds.length} PDVs.`
          : 'Lote criado com sucesso.',
        'success',
      )

      finishSave()
      return
    }

    finishSave()
  } catch (error) {
    if (axios.isAxiosError(error)) {
      snackbar.fromAxiosResponse(error.response?.data)
    } else {
      snackbar.show('Erro ao salvar lote.', 'error')
    }
  } finally {
    loader.decrement()
  }
}

</script>

<style scoped>
.editing-row {
  background: rgba(var(--v-theme-info), 0.05);
}
</style>
